<template>
	<ClientOnly>
		<Teleport to="body">
			<Transition
				enter-active-class="transition duration-150 ease-out motion-reduce:transition-none"
				enter-from-class="opacity-0 -translate-y-2"
				leave-active-class="transition duration-100 ease-in motion-reduce:transition-none"
				leave-to-class="opacity-0 -translate-y-2"
			>
				<div
					v-if="item"
					class="fixed top-28 lg:top-4 z-[60] w-[22.5rem] max-w-[calc(100vw-2rem)] p-5 bg-white rounded-lg shadow-2xl border border-gray-200"
					:class="position === 'left' ? 'left-4' : 'right-4'"
					role="status"
					aria-live="polite"
					@mouseenter="handlePauseDismiss"
					@mouseleave="handleScheduleDismiss"
				>
					<button
						type="button"
						class="absolute right-3 top-3 p-1 text-gray-super-dark rounded-full transition-colors hover:bg-gray-100 hover:text-black"
						:aria-label="$t('Close')"
						@click="handleDismiss"
					>
						<XMarkIcon class="h-5 w-5" />
					</button>

					<div class="flex gap-4">
						<NuxtImg
							:src="itemImage"
							:alt="item.Name || ''"
							class="shrink-0 w-20 h-20 object-contain object-center"
							sizes="80px"
							width="80"
							height="80"
							:provider="item.ImageKey ? undefined : 'storyblok'"
						/>
						<div class="min-w-0 pr-6">
							<p class="mb-1 text-lg font-bold leading-tight">
								{{ $t("added_to_cart_title") }}
							</p>
							<p class="text-sm leading-snug text-gray-super-dark">
								{{ item.Name }}
							</p>
							<p class="mt-2 text-xl font-bold">
								{{ formatPrice(item.PriceDisplayIncVat) }}
							</p>
						</div>
					</div>

					<NuxtLink
						:to="localePath('/checkout')"
						class="button-blue mt-4 py-3 px-6 flex items-center justify-center w-full rounded-4xl font-medium transition-colors hover:bg-blue-light"
						@click="handleDismiss"
					>
						{{ $t("to_checkout") }}
					</NuxtLink>
				</div>
			</Transition>
		</Teleport>
	</ClientOnly>
</template>

<script setup lang="ts">
import { XMarkIcon } from "@heroicons/vue/24/outline";
import { computed, onBeforeUnmount, ref, watch } from "vue";

interface Props {
	position?: "left" | "right";
}
const { position = "right" } = defineProps<Props>();

const DISMISS_AFTER_MS = 7000;

const cartStore = useCartStore();
const productStore = useProductStore();
const localePath = useLocalePath();
const { formatPrice } = useFormatPrice({ groupThousands: true });
const {
	public: {
		norce: { cdn }
	}
} = useRuntimeConfig();

const item = computed(() => cartStore.lastAdded);
const dismissTimeout = ref<ReturnType<typeof setTimeout>>();

const itemImage = computed(() =>
	item.value?.ImageKey
		? `${cdn}/${item.value.ImageKey}?format=webp&w=160`
		: productStore.defaultProductImage || "images/product/no_image.jpeg"
);

const handlePauseDismiss = () => clearTimeout(dismissTimeout.value);

const handleDismiss = () => {
	handlePauseDismiss();
	cartStore.dismissLastAdded();
};

const handleScheduleDismiss = () => {
	handlePauseDismiss();
	dismissTimeout.value = setTimeout(handleDismiss, DISMISS_AFTER_MS);
};

watch(
	() => cartStore.lastAdded,
	(lastAdded) => {
		if (lastAdded) handleScheduleDismiss();
	}
);

onBeforeUnmount(handlePauseDismiss);
</script>
